import { NextPage } from 'next';
import styled from '@emotion/styled';
import Image from 'next/image';

// colors
import { primary } from '@styles/Colors';

// components
import DefaultLayout from '@components/_templates/DefaultLayout';
import Typography from '@components/_atoms/Typography';

// 다국어 지원
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// constants
import { MOBILE_MODE, TABLET_MODE } from '@constants/size';
import Title from '@components/_atoms/Title';
import { TypoVariant } from '@constants/atoms';

const ServiceWrapper = styled.div`
  width: 100%;
  max-width: 1160px;
  padding: 110px 40px;
  margin: 0 auto;

  display: grid;
  row-gap: 24px;

  @media ${TABLET_MODE} {
    max-width: 768px;
  }

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 104px 0;
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const SectionWrapper = styled.div`
  display: grid;
  row-gap: 8px;
`;

const EnImageWrapper = styled.div`
  width: 1080px;
  height: 16114px;
  position: relative;
  margin: 104px auto;
`;

const List = styled.ul`
  padding-left: 24px;

  display: grid;
  row-gap: 8px;
`;
const ListItem = styled.li``;

const Service: NextPage = () => {
  const { i18n } = useTranslation('document');
  const ko = i18n.language === 'ko';

  return (
    <>
      <DefaultLayout>
        {ko ? (
          <ServiceWrapper>
            <TitleWrapper>
              <Title content="서비스 이용약관" />
            </TitleWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH1} color={primary.gray}>
                제1장 총칙
              </Typography>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 1 조 (목적)
              </Typography>
              <Typography variant={TypoVariant.B3} color={primary.gray}>
                이 약관은 마켓오브메테리얼(이하 “회사”)이 운영하는 온라인 플랫폼 steelboso에서 제공하는 전자상거래 관련
                서비스를 이용함에 있어 상품 또는 용역을 거래하는 자 간의 권리, 의무 등 기타 필요사항, 회원과 회사간의
                권리, 의무, 책임사항 및 회원의 서비스 이용절차 등에 관한 사항을 규정함을 목적으로 합니다.
              </Typography>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 2 조 (용어의 정의)
              </Typography>
              <Typography variant={TypoVariant.B3} color={primary.gray}>
                이 약관에서 사용하는 용어의 정의는 다음과 같습니다. 그리고 여기에서 정의되지 않은 이 약관상의 용어의
                의미는 일반적인 거래관행에 따릅니다.
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “온라인 플랫폼”이란 회사가 상품 또는 용역 등(일정한 시설을 이용하거나 용역을 제공받을 수 있는 권리를
                    포함하며, 이하 “상품 등”)을 회원에게 제공하기 위하여 컴퓨터 등 정보통신설비를 이용하여 상품 등을
                    거래할 수 있도록 설정한 가상의 영업장(http://www.steelboso.com 등 회사가 운영하는 웹사이트 및 모바일
                    웹, 앱 등을 모두 포함)을 의미합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “이용자”라 함은 이 약관에 따라 회사가 제공하는 서비스를 받는 회원과 비회원을 말합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “아이디(ID)”라 함은 회원의 식별과 서비스 이용을 위하여 회원이 설정하고 회사가 승인하여 등록된
                    전자우편주소를 말합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “비밀번호(Password)”라 함은 회원의 동일성 확인과 회원의 권익 및 비밀보호를 위하여 회원 스스로가
                    설정하여 온라인 플랫폼에 등록한 문자와 숫자 등의 조합을 말합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “구매확정기간”이란 회원이 거래에 대하여 구매 종료(구매확정, 반품, 교환)의 의사표시를 해야 하는
                    기간으로, 물품수령일로부터 7일이 되는 날(설치상품 등 물품수령일을 확인할 수 없는 때에는
                    발송완료일로부터 21일이 되는 날을 물품수령일로 간주하고 그로부터 7일이 되는 날)까지를 말합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “분쟁조정센터”란 마켓오브메테리얼을 통한 거래에서 분쟁이 발생하면 판매자와 회원이 자율적으로
                    해결하는 것이 원칙이지만, 분쟁의 합리적이고 원활한 조정을 위하여 예외적으로 회사가 설치∙운영하는
                    분쟁조정기구를 말합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 3 조 (약관의 명시와 설명 및 개정)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 이 약관의 내용과 상호 및 대표자 성명, 영업소 소재지 주소(소비자의 불만을 처리할 수 있는
                    주소를 포함), 전화번호, 모사전송번호, 전자우편주소, 사업자등록번호, 통신판매업신고번호,
                    개인정보관리책임자 등을 회원이 쉽게 확인할 수 있도록 온라인 플랫폼의 초기 서비스 화면에 게시합니다.
                    다만, 약관의 구체적 내용은 회원이 연결화면을 통하여 볼 수 있도록 할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 『전자상거래 등에서의 소비자보호에 관한 법률』, 『약관의 규제에 관한 법률』, 『전자문서 및
                    전자거래기본법』, 『전자금융거래법』, 『전자서명법』, 『정보통신망 이용촉진 및 정보보호 등에 관한
                    법률』, 『소비자기본법』 등 관련 법령을 위배하지 않는 범위에서 이 약관을 개정할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    약관의 변경 내용이 고객에게 중대하거나 불리한 경우라면 그 내용을 충분히 알 수 있도록 사전 통지하며
                    30일 이후에 효력이 발생 합니다. 다만 이용자에게 유리한 서비스 기능 변경이나 법적인 사유로 인한
                    변경에 한하여 즉시 발효됩니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    제3항에 의해 변경된 약관은 관련 법령에 특별한 규정 기타 부득이한 사유가 없는 한 그 적용일자 이전으로
                    소급하여 적용되지 아니합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    제3항에 따라 공지된 적용일자 이후에 회원이 명시적으로 거부의사를 표명하지 않을 경우에는 개정된
                    약관에 동의하는 것으로 간주하며, 변경된 약관에 동의하지 않는 회원은 회원 탈퇴를 요청할 수 있습니다.
                    제3항의 조치에도 불구하고 약관의 개정 사실을 알지 못함으로써 발생하는 회원의 피해에 대하여 회사는
                    책임을 지지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    “회사는 제공하는 서비스 내의 개별 서비스에 대한 별도의 약관 또는 이용조건을 둘 수 있으며 개별
                    서비스에서 별도로 적용되는 약관에 대한 동의는 회원이 개별 서비스를 최초로 이용할 경우 별도의
                    동의절차를 거치게 됩니다. 이 경우 개별 서비스에 대한 이용약관 등이 이 약관에 우선합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    이 약관에서 정하지 아니한 사항과 이 약관의 해석에 관하여는 『전자상거래 등에서의 소비자보호에 관한
                    법률』, 『약관의 규제에 관한 법률』, 공정거래위원회가 정하는 『전자상거래 등에서의 소비자 보호지침』
                    등 관련 법령 및 상관례에 따릅니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    이 약관의 규정이 대한민국의 강행법규와 상충되는 경우에는 그 강행법규에 따릅니다. 이 경우 다른 조항의
                    효력에는 영향을 미치지 아니합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 4 조 (서비스의 제공 및 변경)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 다음 각 호와 같은 서비스를 제공합니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 전자상거래 서비스(통신판매중개서비스 포함) 및 이에 수반 되는 기타 온라인 오프라인 서비스
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 기타 회사가 정하는 서비스 또는 업무
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 상품 등의 품절 또는 기술적 사양의 변경 등의 경우에는 장차 체결되는 계약에 의해 제공할 상품
                    등의 내용을 변경할 수 있습니다. 이 경우에는 변경된 상품 등의 내용을 명시하여 현재의 상품 등의 내용을
                    게시한 곳에 즉시 공지합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 제공하기로 회원과 계약을 체결한 서비스의 내용을 상품 등의 품절 또는 기술적 사양의 변경 등의
                    사유로 변경할 경우에는 그 사유를 회원에게 통지 가능한 주소로 즉시 통지합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 거래 플랫폼을 제공함에 있어 중개역할을 이행하며, 회사 운영 기준(컨설팅, 익명거래, 디지털 화
                    된 서비스 관리)을 따릅니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 5 조 (서비스의 중단)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 천재지변 또는 이에 준하는 불가항력, 컴퓨터 등 정보통신설비의 보수점검·교체, 고장, 통신의 두절
                    등의 사유가 발생한 경우에는 서비스의 제공을 일시적으로 중단할 수 있습니다
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 제1항의 사유로 서비스의 제공이 일시적으로 중단됨으로 인하여 회원이 입은 손해에 대하여
                    배상합니다. 단, 회사가 고의 또는 과실이 없음을 입증하는 경우에는 그러하지 아니합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    사업 종목의 전환, 사업의 종료 또는 포기, 회사의 폐업, 합병, 분할, 영업양도 등의 이유로 서비스를
                    제공할 수 없게 되는 경우에는 회사는 제8조에 정한 방법으로 회원에게 통지하고 당초 회사에서 제시한
                    조건에 따라 회원에게 보상합니다. 다만, 회사가 보상기준 등을 고지하지 아니한 경우에는 회사에서
                    통용되는 통화가치에 상응하는 현물 또는 현금으로 회원에게 지급할 수 있습니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 6 조 (회원가입 등)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원 가입을 희망하는 자는 회사가 정한 가입양식에 따라 회원정보를 기입한 후 이 약관에 동의한다는
                    의사표시를 함으로써 회원가입을 신청합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 제1항과 같이 회원으로 가입할 것을 신청한 자 중 다음 각 호에 해당하지 않는 한 회원으로
                    등록합니다. 만약 회사가 회원가입신청의 승인을 거부하거나 유보하는 경우, 회사는 이를 제8조에 따라
                    회원에게 통지합니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 가입신청자가 이 약관 제7조 제3항에 의하여 이전에 회원자격을 상실한 적이 있는 경우.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 회원의 요청에 의하여 탈퇴한 때로부터 1개월이 지나지 아니한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 가입신청자가 기입한 회원정보에 명백한 허위 기재, 누락 또는 오기가 있는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 회사가 본인확인 절차를 실시할 경우에 본인 확인이 되지 않거나 본인이 아님이 확인된 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 가입신청자가 만 14세 미만인 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 기타 가입신청자를 회원으로 등록하는 것이 온라인 플랫폼의 기술상 불가능하거나, 온라인 플랫폼의
                    운영 또는 유지·관리에 현저한 지장을 준다고 판단되는 경우
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원가입계약의 성립시기는 회사의 승낙이 가입신청자에게 도달한 시점으로 합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 회원정보에 변경이 있는 경우, 회원정보 수정 등의 방법으로 그 변경사항을 반영하여야 합니다.
                    회원정보 수정을 하지 않음으로써 발생하는 회원의 손해에 대하여 회사는 아무런 책임을 지지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 『청소년 보호법』 등 관련 법령에 따른 등급 및 연령제한 준수를 위하여 회원의 서비스 이용 또는
                    상품 등 구매를 제한하거나, 회원에게 나이 및 본인확인 등의 절차를 요구할 수 있습니다
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 7 조 (회원 탈퇴 및 자격 상실 등)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 회사에 언제든지 탈퇴를 요청할 수 있으며, 회사는 회원의 회원탈퇴 요청 즉시 회원탈퇴를
                    처리합니다. 회원탈퇴 시 회원자격이 상실되며, 회사가 제공하는 각종 이벤트 혜택 등이 소멸됩니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원이 다음 각 호의 사유에 해당하는 경우, 회사는 회원의 자격을 제한 또는 상실시킬 수 있으며, 회원
                    자격을 상실할 경우 회원으로서의 모든 혜택은 전부 소멸됩니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 회원정보에 허위 내용을 입력하거나 타인의 정보를 도용한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 온라인 플랫폼을 이용하여 구입한 재화 등의 대금, 기타 온라인 플랫폼 이용과 관련하여 회원이
                    부담하는 채무를 기일에 지급하지 않는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 다른 사람의 온라인 플랫폼 이용을 방해하거나 그 정보를 도용하는 등 전자상거래 질서를 위협하는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 주소, 전자우편 등 회원정보를 잘못 입력하거나 변경하지 않는 등 회원의 귀책사유로 인하여 회사가
                    상당한 노력을 기울였음에도 불구하고 회원에게 통지 또는 연락을 할 수 없게 된 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 이 약관 제12조 회원의 의무를 위반하는 행위를 하는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 이 약관의 규정 또는 회사의 이용정책 등을 위반하거나 법령 기타 공서양속에 반하는 행위를 하는 경우
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 회원 자격을 제한 시킨 후 동일한 행위가 2회 이상 반복되거나 30일 이내에 그 사유가 시정되지
                    아니하는 경우 회사는 회원 자격을 상실시킬 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 회원 자격을 상실시키는 경우에는 회원 등록을 말소하며, 이때 회사는 거래 안전을 위하여 해당
                    회원의 소유로 확인되는 아이디의 회원 등록을 일괄 말소할 수 있습니다. 이 경우 회사는 이를 제8조에
                    따라 회원에게 통지하고, 회원 등록 말소 전 최소한 30일 이상의 기간을 정하여 소명할 기회를 부여합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 8 조 (회원에 대한 통지)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 회원에 대한 통지를 하는 경우, 회원이 회사와 미리 약정하여 지정한 방법에 따라 전자우편, SMS,
                    전화, 팩스 등으로 할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 불특정다수 회원에 대한 통지의 경우 7일 이상 온라인 플랫폼의 공지사항(고객센터)에 게시함으로써
                    개별 통지에 갈음할 수 있습니다. 다만, 회원 본인의 거래에 관련하여 중대한 영향을 미치는 사항에
                    대하여는 개별 통지합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 9 조 (정보의 제공 및 광고의 게재)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원이 서비스 이용 중 필요하다고 인정되는 다양한 정보를 게시판에 공지하거나 전자우편이나 SMS,
                    전화, PUSH 등의 방법으로 회원에게 제공할 수 있습니다. 그리고 회원은 법령에 따라 회사에게 부과된
                    의무이행을 위한 절차안내, 고객문의 등에 대한 답변, 기타 서비스 이용에 필요한 안내 등을 제외하고는
                    언제든지 전자우편 등에 대해서 수신 거절을 할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 서비스의 운영과 관련하여 서비스 화면, 전자우편 등에 광고를 게재할 수 있습니다. 광고가 포함된
                    전자우편 등을 수신한 회원은 수신거절을 할 수 있으며, 회사는 수신거절을 위한 방법을 회원에게
                    제공합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 10 조 (회사의 의무)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 이 약관이 금지하거나 관련 법령 기타 공서양속에 반하는 행위를 하지 않으며 이 약관이 정하는
                    바에 따라 지속적이고, 안정적으로 서비스를 제공하는 데 최선을 다합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원이 안전하게 서비스를 이용할 수 있도록 회원의 개인정보(신용정보 포함)보호를 위한 보안
                    규정을 갖추어야 하며, 개인정보처리방침을 공시하고 준수합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원이 동의하지 않는 영리목적의 광고성 전자우편, PUSH 등을 발송하지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원으로부터 제기되는 의견이나 불만이 객관적으로 정당하다고 인정될 경우에는 회원의 불만을
                    해결하기 위해 노력합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 11 조 (회원의 아이디 및 비밀번호에 대한 의무)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    아이디와 비밀번호에 관한 관리책임은 회원 본인에게 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 자신의 아이디 및 비밀번호를 제3자에게 제공, 공개하거나 제3자가 이용하도록 해서는 안 됩니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원이 자신의 아이디 및 비밀번호의 유출 또는 제3자 사용에 대해 인지하고도 비밀번호를 변경하지 않은
                    경우 또는 이와 같은 사정을 회사에 통지 하지 않거나 회사의 조치에 응하지 않은 경우 등 회원이 본 조의
                    의무를 준수하지 않아 발생하는 모든 손실이나 손해 등 불이익에 대한 책임은 회원에게 있습니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 12 조 (회원의 의무)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 이 약관의 규정, 회사의 이용정책, 이용안내 사항 및 관련 법령 등을 준수하여야 하며, 기타 회사
                    업무에 방해되는 행위를 하여서는 안 됩니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 다음 각 호의 행위를 하여서는 안 됩니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 회원이 회사가 제공하는 서비스를 이용하지 아니하고 판매자와 직접 거래하는 행위(이하 “직거래”) 또는
                    이를 유도하는 행위
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 회사 또는 판매자의 판매 활동을 방해하거나 온라인 플랫폼을 이용하여 부당한 이득을 취하는 등
                    통상적인 전자상거래 관행에 부합하지 않는 거래상의 부정 행위
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 상품의 판매 또는 용역의 제공을 가장한 현금 융통 등 『여신전문금융업법』, 『정보통신망 이용촉진 및
                    정보보호 등에 관한 법률』 및 기타 관계 법령에 위배되거나 비정상적인 결제 행위
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 회사가 제공하는 서비스 이용방법에 의하지 아니하고 비정상적인 방법으로 서비스를 이용하거나 회사의
                    정보처리시스템에 접근하는 행위
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 회사가 제공하는 서비스와 관련 없거나 비합리적인 요청, 공서양속에 반하는 행위 등으로 회사의 업무를
                    방해하는 행위
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 기타 위 각 호에 준하여 회사 또는 제3자의 권리를 침해 또는 침해할 우려가 있거나, 전자상거래 질서를
                    위협하는 행위
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 제2항을 위반한 회원에 대하여 계약 해제, 회원 자격의 제한, 현금 환불 보류 등의 조치 및 민∙형사
                    상의 법적 조치 등을 취할 수 있습니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 13 조 (개인정보보호)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원의 개인정보 수집 시 서비스 제공을 위하여 필요한 범위에서 최소한의 개인정보를 수집합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원가입 시 구매계약이행에 필요한 정보를 미리 수집하지 않습니다. 다만, 관련 법령상 의무이행을
                    위하여 구매계약 이전에 본인확인이 필요한 경우로서 최소한의 특정 개인정보를 수집하는 경우에는
                    그러하지 아니합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원의 개인정보를 수집·이용하는 때에는 당해 회원에게 그 목적을 고지하고 동의를 받습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 수집된 개인정보를 목적 외의 용도로 이용할 수 없으며, 새로운 이용목적이 발생한 경우 또는
                    제3자에게 제공하는 경우에는 이용·제공단계에서 당해 회원에게 그 목적을 고지하고 동의를 받습니다.
                    다만, 관련 법령에 달리 정함이 있는 경우에는 예외로 합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 제3항과 제4항에 의해 회원의 동의를 받아야 하는 경우에는 개인정보 관리 책임자의 신원(소속,
                    성명 및 전화번호, 기타 연락처), 정보의 수집목적 및 이용목적, 제3자에 대한 정보제공 관련사항(제공받은
                    자, 제공목적 및 제공할 정보의 내용) 등 『정보통신망 이용촉진 및 정보보호 등에 관한 법률』 에서
                    규정한 사항을 미리 명시하거나 고지해야 하며 회원은 언제든지 이 동의를 철회할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 언제든지 회사가 가지고 있는 자신의 개인정보에 대해 열람 및 오류 정정을 요구할 수 있으며
                    회사는 이에 대해 지체 없이 필요한 조치를 취할 의무를 집니다. 회원이 오류의 정정을 요구한 경우에는
                    회사는 그 오류를 정정할 때까지 당해 개인정보를 이용하지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 개인정보 보호를 위하여 회원의 개인정보를 취급하는 자를 최소한으로 제한하여야 하며 개인정보를
                    제공받은 제3자는 개인정보의 수집목적 또는 제공받은 목적을 달성한 때에는 당해 개인정보를 지체 없이
                    파기합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 개인정보의 수집·이용·제공에 관한 동의란을 미리 선택한 것으로 설정해두지 않습니다. 또한
                    개인정보의 수집·이용·제공에 관한 동의 거절 시 제한되는 서비스를 구체적으로 명시하고, 필수수집항목이
                    아닌 개인정보의 수집·이용·제공에 관한 회원의 동의 거절을 이유로 회원가입 등 서비스 제공을 제한하거나
                    거절하지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사의 온라인 플랫폼(제2조 제1호에서 정한 범위 내) 이외의 링크된 온라인 플랫폼에서는 회사의
                    개인정보처리방침이 적용되지 않습니다. 링크된 온라인 플랫폼 및 상품 등을 제공하는 제3자의 개인정보
                    취급과 관련하여서는 해당 온라인 플랫폼 및 제3자의 개인정보처리방침을 확인할 책임이 회원에게 있으며,
                    회사는 이에 대하여 책임을 부담하지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 다음과 같은 경우에 법이 허용하는 범위 내에서 회원의 개인정보를 제3자에게 제공할 수 있습니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 수사기관이나 기타 정부기관으로부터 적법한 절차에 따른 정보제공을 요청 받은 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 회원이 관련 법령을 위반하는 등의 부정행위를 확인하기 위해 필요한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 판매자 또는 배송업체 등에게 거래 및 배송 등에 필요한 최소한의 회원의 정보(성명, 주소, 전화번호)를
                    제공하는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 구매가 성사된 때에 그 이행을 위하여 필요한 경우와 구매가 종료된 이후에도 반품, 교환, 환불, 취소
                    등을 위하여 필요한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 기타 관련 법령에 의해 요구되는 경우
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    기타 본 조에서 정하지 않은 사항은 회사가 마련한 “개인정보 처리방침”에 따릅니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 14 조 (지급방법)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    온라인 플랫폼에서 구매한 상품 등에 대한 대금지급 방법은 다음 각 호의 방법 중 가용한 방법으로 할 수
                    있습니다. 단, 회사는 회원의 지급방법에 대하여 상품 등의 대금에 어떠한 명목의 수수료도 추가하여
                    징수할 수 없습니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 폰뱅킹, 인터넷뱅킹 등 가상계좌를 통한 각종 계좌이체
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 선불카드, 직불카드, 신용카드 등의 각종 카드 결제
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 온라인 무통장입금
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 수령 시 대금지급
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 회사와 계약을 맺었거나 회사가 인정한 상품권에 의한 결제
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 외상매출 채권
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    7. 기타 전자적 지급 방법에 의한 대금 지급 등
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원이 구매 대금의 결제와 관련하여 입력한 정보 및 그 정보와 관련하여 발생한 책임과 불이익은 전적으로
                    회원이 부담합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    대금지급은 제1항의 대금지급 방법 이외에도 다음 각 호의 방법 중 가용한 방법으로 할 수 있습니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 회원이 유효한 카드를 제시하여 거래를 요청하는 경우회사가 신용카드 단말기 또는 기타 카드사가 정한
                    방법으로 카드사로부터 거래승인을 받는 방법
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 회원이 현금을 직접 교부하여 지급
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 기타 지급 방법 중 회사가 승인하는 방법에 의한 대금 지급 등
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH1} color={primary.gray}>
                제2장 전자상거래 서비스
              </Typography>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 15 조 (구매신청 및 개인정보 제공 동의 등)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 온라인 플랫폼 상에서 다음 또는 이와 유사한 방법에 의하여 구매를 신청하며, 회사는 회원이
                    구매신청을 함에 있어서 다음의 각 내용을 알기 쉽게 제공하여야 합니다.
                  </Typography>
                  <br />

                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 상품 등의 검색 및 선택
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 받는 사람의 성명, 주소, 전화번호, 전자우편주소(또는 이동전화번호) 등의 입력
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 약관내용, 청약철회권이 제한되는 서비스, 운송비용 등의 비용부담과 관련한 내용에 대한 확인
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 이 약관에 동의하고 위 제3호의 사항을 확인하거나 거부하는 표시(예. 마우스 클릭)
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 상품 등의 구매신청 및 이에 관한 확인 또는 회사의 확인에 대한 동의
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 결제방법의 선택
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 제3자에게 회원의 개인정보를 제공할 필요가 있는 경우 실제 구매신청 시 회원의 동의를 받아야
                    하며, 회원가입 시 미리 포괄적으로 동의를 받지 않습니다. 이때 회사는 제공되는 개인정보 항목, 제공받는
                    자, 제공받는 자의 개인정보 이용목적 및 보유·이용기간 등을 회원에게 명시하여야 합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사가 제3자에게 회원의 개인정보 처리(개인정보의 수집, 생성, 연계, 연동, 기록, 저장, 보유, 가공,
                    편집, 검색, 출력, 정정(訂正), 복구, 이용, 제공, 공개, 파기(破棄), 그 밖에 이와 유사한 행위를 포함)
                    업무를 위탁할 필요가 있는 경우 실제 구매신청 시 회원의 동의를 받아야 하며, 회원가입 시 미리
                    포괄적으로 동의를 받지 않습니다. 이때 회사는 개인정보 처리위탁을 받는 자, 개인정보 처리위탁을 하는
                    업무의 내용 등을 회원에게 명시하여야 합니다. 다만, 『정보통신망이용촉진 및 정보보호 등에 관한
                    법률』에 달리 정함이 있는 경우에는 그에 따릅니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 16 조 (계약의 성립)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 제15조와 같은 구매신청에 대하여 다음 각 호에 해당하면 계약을 승낙하지 않을 수 있습니다. 다만,
                    미성년자와 계약을 체결하는 경우에는 법정대리인의 동의를 얻지 못하면 미성년자 본인 또는 법정대리인이
                    계약을 취소할 수 있다는 내용을 고지하여야 합니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 신청 내용에 허위, 기재누락, 오기가 있는 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 미성년자가 담배(전자담배 포함), 주류, 성인용품 등 청소년보호법에서 금지하는 재화 등을 구매하는
                    경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 제7조에 따라 회원 자격이 제한 또는 상실된 회원이 구매신청 한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 구매신청 회원이 재판매의 목적으로 상품 등을 중복 구매하는 등 온라인 플랫폼의 거래질서를 방해한
                    경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 관할청의 처분 등으로 상품 등의 판매가 중단된 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 기타 구매신청에 승낙하는 것이 회사의 기술상 현저히 지장이 있다고 판단하는 경우
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사의 승낙이 제17조 제1항의 수신확인통지 형태로 회원에게 도달한 시점에 계약이 성립한 것으로 봅니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사의 승낙의 의사표시에는 회원의 구매신청에 대한 확인 및 판매가능 여부, 구매신청의 정정, 취소 등에
                    관한 정보 등을 포함하여야 합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    계약이 성립한 후 회사가 제1항 각 호의 사유를 발견한 경우 회사는 즉시 계약을 해제 또는 취소할 수
                    있으며, 계약 해제 또는 취소 시 회사는 회원이 상품 대금을 지급한 날로부터 3영업일 이내에 환급하거나
                    환급에 필요한 조치를 취합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 17 조 (수신확인통지·구매신청 변경 및 취소)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원의 구매신청이 있는 경우 회원에게 수신확인통지를 합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    수신확인통지를 받은 회원은 의사표시의 불일치 등이 있는 경우에는 수신확인통지를 받은 후 즉시 구매신청
                    변경 및 취소를 요청할 수 있고, 상품의 제작이 이미 진행 된 경우에는 취소가 불가합니다. 다만, 회원이
                    이미 대금을 지불한 경우에는 제20조의 청약철회 등에 관한 규정에 따릅니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 18 조 (상품 등의 공급)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원과 상품 등의 공급시기에 관하여 별도의 약정이 없는 이상, 회원이 청약을 한 날로부터 7일
                    이내에 상품 등을 배송할 수 있도록 주문제작, 포장 등 기타의 필요한 조치를 취합니다. 다만, 회사가 이미
                    상품 등의 대금의 전부 또는 일부를 받은 경우에는 대금의 전부 또는 일부를 받은 날부터 3영업일 이내에
                    조치를 취합니다. 이때 회사는 회원이 상품 등의 공급절차 및 진행사항을 확인할 수 있도록 적절한 조치를
                    취합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원이 구매한 상품 등에 대해 배송수단, 수단별 배송비용, 부담자, 수단별 배송예상기간 등을
                    명시합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사와 회원 간에 상품 등의 인도 또는 제공 시기에 관하여 별도의 약정이 있는 경우에는 해당 약정이 이
                    약관에 우선합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 19 조 (환급)
              </Typography>
              <Typography variant={TypoVariant.B3} color={primary.gray}>
                회사는 회원이 구매신청한 상품 등이 품절 등의 사유로 인도 또는 제공할 수 없을 때에는 지체 없이 그 사유를
                회원에게 통지하고, 사전에 상품 등의 대금을 받은 경우에는 대금을 받은 날로부터 3영업일 이내에 환급하거나
                환급에 필요한 조치를 취합니다.
              </Typography>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 20 조 (청약철회 등)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사와 상품 등의 구매에 관한 계약을 체결한 회원은 『전자상거래 등에서의 소비자보호에 관한 법률』
                    제13조 제2항에 따른 계약내용에 관한 서면을 받은 날(그 서면을 받은 때보다 상품 등의 공급이 늦게
                    이루어진 경우에는 상품 등을 공급받거나 상품 등의 공급이 시작된 날을 말함)부터 7일 이내에는 청약을
                    철회할 수 있습니다. 다만, 청약철회에 관하여 『전자상거래 등에서의 소비자보호에 관한 법률』에 달리
                    정함이 있는 경우에는 동 법 규정에 따릅니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 다음 각 호의 어느 하나에 해당하는 경우에는 회사의 의사에 반하여 제1항에 따른 청약철회 등을 할
                    수 없습니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 회원에게 책임 있는 사유로 상품 등이 멸실 또는 훼손된 경우(다만, 상품 등의 내용을 확인하기 위하여
                    포장 등을 훼손한 경우는 제외함)
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 회원의 사용 또는 일부 소비에 의하여 상품 등의 가치가 현저히 감소한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    3. 시간의 경과에 의하여 재판매가 곤란할 정도로 상품 등의 가치가 현저히 감소한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    4. 복제 가능한 상품 등의 포장을 훼손한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    5. 회사가 상품 등의 청약철회 등의 제한에 관해 사전에 고지한 경우
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    6. 주문에 따라 개별적으로 생산되는 상품 등 그에 대하여 청약철회 등을 인정할 경우 판매자에게 회복할
                    수 없는 중대한 피해가 예상되는 경우로서, 사전에 해당 거래에 대하여 별도로 그 사실을 고지하고 회원의
                    서면(전자문서를 포함)에 의한 동의를 받은 경우
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 제1항 및 제2항에도 불구하고 상품의 내용이 표시∙광고 내용과 다르거나 계약내용과 다르게 이행된
                    경우에는 해당 상품 등을 공급받은 날부터 3개월 이내, 그 사실을 안 날 또는 알 수 있었던 날부터 30일
                    이내에 청약철회 등을 할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원으로부터 교환 또는 반품의 의사표시를 받은 경우에는 즉시 그 사실을 판매자에게 통보합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원은 제1항 또는 제3항에 따라 교환 신청을 하더라도 판매자에게 교환할 상품의 재고가 없는 경우에는
                    해당 상품 등의 교환을 받을 수 없습니다. 이 경우 해당 교환 신청은 반품으로 처리됩니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    반품이나 교환에 필요한 배송비 및 기타 비용은 귀책사유가 있는 당사자가 부담합니다. 이 경우 상품 등의
                    하자 또는 오배송으로 인한 반품이나 교환의 경우에는 판매자가 그 비용을 부담하나, 회원의 단순 변심으로
                    인한 반품이나 교환의 경우에는 회원이 그 비용을 부담합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    판매자는 회원이 배송상의 문제로 손해를 입은 경우에는 그에 대한 책임을 부담합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 판매자가 회원의 결제완료에 대한 통지를 받은 이후에도 상당 기간 배송 절차 등을 이행하지
                    아니하여 회원이 청약철회 등을 하거나 개별적인 정책으로 미리 정한 자동환불처리 사안에 해당하는
                    경우에는 해당 거래를 취소하고 결제대금예치서비스 이용 중인 결제대금을 회원에게 환불할 수 있습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    판매자가 회원의 교환 또는 반품 신청에 대하여 적극적으로 임하지 않거나 이를 지연하는 경우, 회사는
                    회원에게 그 교환 또는 반품 신청의 사유를 파악하여 정당한 사유가 있는 경우에는 해당 거래를 취소하는
                    한편, 예치 중인 결제대금을 회원에게 환불할 수 있습니다. 다만 회원의 교환 또는 반품신청에 정당한
                    사유가 없는 경우에는 그러하지 아니합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회원이 교환 또는 반품신청을 한 날로부터 14일이 지날 때까지 이미 수령한 상품을 판매자에게 반환하지
                    아니 하거나 전화, 전자우편 등으로 연락되지 아니하는 경우, 해당 회원의 교환 또는 반품 신청은 효력을
                    상실합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 청약철회 등으로 인하여 결제대금을 환불하는 경우에는 그 청약철회 등을 한 날(단, 상품 등이
                    배송된 경우에는 판매자가 상품 등을 반환 받은 날)로부터 3영업일 이내에 환불에 필요한 조치를 합니다.
                    신용카드로 결제가 이루어진 경우 환불은 해당 신용카드 결제 취소를 통해서만 가능하고, 어떠한 경우에도
                    현금으로 환불되지 아니합니다.
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>

            <SectionWrapper>
              <Typography variant={TypoVariant.SH2} color={primary.gray}>
                제 21 조 (청약철회 등의 효과)
              </Typography>
              <List>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 회원으로부터 상품 등을 반환 받은 경우 3영업일 이내에 이미 지급받은 상품 등의 대금을
                    환급합니다. 이 경우 회사가 회원에게 상품 등의 대금 환급을 지연한 때에는 그 지연기간에 대하여
                    『전자상거래 등에서의 소비자보호에 관한 법률 시행령』 제17조의3에서 정하는 지연이자율을 곱하여
                    산정한 지연이자를 지급합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 위 대금을 환급함에 있어서 회원이 신용카드 또는 전자화폐 등의 결제수단으로 상품 등의 대금을
                    지급한 때에는 지체없이 당해 결제수단을 제공한 사업자로 하여금 상품 등의 대금의 청구를 정지하거나
                    결제를 취소하도록 요청합니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    회사는 이미 상품 등이 일부 사용 또는 일부 소비된 경우에는 일부 소비에 의하여 회원이 얻은 이익 또는
                    그 상품 등의 공급에 소요된 비용에 상당하는 금액으로서 아래의 각 호에 해당하는 금액의 지급을 회원에게
                    청구할 수 있습니다.
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    1. 상품 등의 사용으로 인하여 소모성 부품의 재판매가 곤란하거나 재판매가격이 현저히 하락하는 경우에는
                    당해 소모성 부품의 공급에 소요된 비용
                  </Typography>
                  <br />
                  <Typography variant={TypoVariant.B4} color={primary.gray}>
                    2. 다수의 동일한 가분물로 구성된 상품 등의 경우에는 회원의 일부 소비로 인하여 소비된 부분의 공급에
                    소요된 비용
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    제20조제1항에 따른 청약철회 등의 경우 공급받은 상품 등의 반환에 필요한 제반 비용은 회원이
                    부담합니다. 회사는 회원에게 청약철회 등을 이유로 위약금 또는 손해배상을 청구하지 않습니다.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    서비스 이용약과 버전 번호: 3.1
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    현재 개인정보 처리방침 공고일자: 2023년 2월 05일
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant={TypoVariant.B3} color={primary.gray}>
                    현재 개인정보 처리방침 시행일자: 2023년 3월 05일
                  </Typography>
                </ListItem>
              </List>
            </SectionWrapper>
          </ServiceWrapper>
        ) : (
          <EnImageWrapper>
            <Image src="/assets/images/service-en.png" alt="policy-en" layout="fill" />
          </EnImageWrapper>
        )}
      </DefaultLayout>
    </>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['document', 'common', 'component'])),
  },
});

export default Service;
